'use client';
import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const revenueData = [
  { date: 'Apr 1', revenue: 7840, bookings: 28 },
  { date: 'Apr 2', revenue: 9120, bookings: 34 },
  { date: 'Apr 3', revenue: 6540, bookings: 22 },
  { date: 'Apr 4', revenue: 11200, bookings: 41 },
  { date: 'Apr 5', revenue: 8900, bookings: 31 },
  { date: 'Apr 6', revenue: 13400, bookings: 48 },
  { date: 'Apr 7', revenue: 10200, bookings: 37 },
  { date: 'Apr 8', revenue: 7600, bookings: 26 },
  { date: 'Apr 9', revenue: 9800, bookings: 35 },
  { date: 'Apr 10', revenue: 12100, bookings: 44 },
  { date: 'Apr 11', revenue: 8400, bookings: 29 },
  { date: 'Apr 12', revenue: 14800, bookings: 52 },
  { date: 'Apr 13', revenue: 11600, bookings: 40 },
  { date: 'Apr 14', revenue: 9200, bookings: 33 },
  { date: 'Apr 15', revenue: 10500, bookings: 38 },
  { date: 'Apr 16', revenue: 13200, bookings: 47 },
  { date: 'Apr 17', revenue: 7900, bookings: 27 },
  { date: 'Apr 18', revenue: 11400, bookings: 42 },
  { date: 'Apr 19', revenue: 15200, bookings: 55 },
  { date: 'Apr 20', revenue: 12800, bookings: 46 },
  { date: 'Apr 21', revenue: 9600, bookings: 35 },
  { date: 'Apr 22', revenue: 6200, bookings: 18 },
];

const CustomTooltip = ({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ value: number; name: string }>;
  label?: string;
}) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-border rounded-xl shadow-modal px-4 py-3 text-sm">
        <p className="font-semibold text-foreground mb-2">{label}</p>
        <div className="space-y-1">
          <p className="text-primary font-bold tabular-nums">${payload[0]?.value?.toLocaleString()}</p>
          <p className="text-muted-foreground text-xs">{payload[1]?.value} bookings</p>
        </div>
      </div>
    );
  }
  return null;
};

export default function RevenueChart() {
  return (
    <div className="bg-white rounded-xl border border-border shadow-card p-6 h-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-display font-semibold text-base text-foreground">Daily Revenue — April 2026</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Total bookings revenue by day</p>
        </div>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 bg-primary rounded-full inline-block" />
            Revenue
          </span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={revenueData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <defs>
            <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="hsl(211, 65%, 28%)" stopOpacity={0.15} />
              <stop offset="95%" stopColor="hsl(211, 65%, 28%)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 20%, 88%)" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: 'hsl(215, 15%, 50%)' }}
            tickLine={false}
            axisLine={false}
            interval={3}
          />
          <YAxis
            tick={{ fontSize: 11, fill: 'hsl(215, 15%, 50%)' }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
            width={40}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="revenue"
            stroke="hsl(211, 65%, 28%)"
            strokeWidth={2}
            fill="url(#revenueGradient)"
            dot={false}
            activeDot={{ r: 5, fill: 'hsl(211, 65%, 28%)', strokeWidth: 2, stroke: '#fff' }}
          />
          <Area
            type="monotone"
            dataKey="bookings"
            stroke="hsl(40, 65%, 55%)"
            strokeWidth={1.5}
            fill="none"
            dot={false}
            activeDot={{ r: 4, fill: 'hsl(40, 65%, 55%)', strokeWidth: 2, stroke: '#fff' }}
            strokeDasharray="4 2"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}